import type { Icon } from '@phosphor-icons/react'
import { Briefcase, Coffee, Compass, Envelope, FileText, House, SealCheck, ShieldCheck, Stack, User } from '@phosphor-icons/react'
import { workCount } from '@/content/work'
import { externalLinks } from '@/content/site'

/**
 * The route registry: the one list that drives the router, the sidebar, the search palette
 * and every page title. Add a page = add one entry here.
 */

export type NavGroupId = 'menu' | 'discover' | 'general'

export const NAV_GROUPS: { id: NavGroupId; label: string }[] = [
  { id: 'menu', label: 'Menu' },
  { id: 'discover', label: 'Discover' },
  { id: 'general', label: 'General' },
]

export type PageRoute = {
  kind: 'page'
  id: string
  path: string
  /** Short name for the sidebar and search. */
  label: string
  /** Unique document title. */
  title: string
  description: string
  icon: Icon
  /** Omit to keep the page out of the sidebar (legal pages). */
  group?: NavGroupId
  badge?: number
  /** A Home section id: the sidebar and search scroll Home to it instead of opening `path`. */
  section?: string
  /** Still the RouteStage placeholder: kept out of the sitemap (RouteStage also marks it noindex).
   *  Remove when the page is built and registered in router.tsx PAGE_COMPONENTS. */
  draft?: true
}

export type ExternalRoute = {
  kind: 'external'
  id: string
  href: string
  label: string
  description: string
  icon: Icon
  group: NavGroupId
}

export type NavEntry = PageRoute | ExternalRoute

const SUFFIX = 'Kenneth Villar'

export const pages: PageRoute[] = [
  {
    kind: 'page',
    id: 'home',
    path: '/',
    label: 'Home',
    title: `${SUFFIX} | GoHighLevel Automation for Coaches and Agencies`,
    description: 'Certified GoHighLevel Admin (Member #7009). Workflows, pipelines and funnels that follow up with every lead and book the call.',
    icon: House,
    group: 'menu',
  },
  {
    kind: 'page',
    id: 'work',
    draft: true,
    path: '/work',
    label: 'Work',
    title: `Work: GHL Systems, Funnels and Apps | ${SUFFIX}`,
    description: 'A GoHighLevel ticketing system, funnel and website samples, Android apps, Chrome extensions, and AI agents.',
    icon: Briefcase,
    group: 'menu',
    badge: workCount,
    section: 'work',
  },
  {
    kind: 'page',
    id: 'services',
    draft: true,
    path: '/services',
    label: 'Services',
    title: `Services: Automation, CRM, Funnels, Sites | ${SUFFIX}`,
    description: 'GHL automation, CRM setup, AI-assisted funnels, websites and small apps for coaches and agencies.',
    icon: Stack,
    group: 'menu',
    section: 'services',
  },
  {
    kind: 'page',
    id: 'about',
    draft: true,
    path: '/about',
    label: 'About',
    title: `About Kenneth Villar, Certified GHL Admin #7009`,
    description: 'Eleven years in customer support, now building GoHighLevel automation and AI tools. Based in the Philippines.',
    icon: User,
    group: 'menu',
    section: 'about',
  },
  {
    kind: 'page',
    id: 'proof',
    path: '/proof',
    label: 'Proof',
    title: `Client Proof and Feedback | ${SUFFIX}`,
    description: 'The client accounts I run, client video testimonials, and written feedback from the community.',
    icon: SealCheck,
    group: 'discover',
    section: 'proof',
  },
  {
    kind: 'page',
    id: 'kape-tools',
    path: '/kape-tools',
    label: 'Kape.Tools',
    title: `Kape.Tools, the Freelancer Workspace | ${SUFFIX}`,
    description: 'The workspace I built for Filipino freelancers and VAs: task board, client CRM, browser tools, job boards and a lead finder.',
    icon: Coffee,
    group: 'discover',
    section: 'kape-tools',
  },
  {
    kind: 'page',
    id: 'contact',
    path: '/contact',
    label: 'Contact',
    title: `Contact: Get a Written Automation Plan | ${SUFFIX}`,
    description: 'Tell me where your leads stop. Reply within one business day with a plan or a straight no.',
    icon: Envelope,
    group: 'general',
    section: 'contact',
  },
  {
    kind: 'page',
    id: 'privacy',
    path: '/privacy',
    label: 'Privacy Policy',
    title: `Privacy Policy | ${SUFFIX}`,
    description: 'What this site stores, what it does not, and how to ask for your data.',
    icon: ShieldCheck,
  },
  {
    kind: 'page',
    id: 'terms',
    path: '/terms',
    label: 'Terms of Service',
    title: `Terms of Service | ${SUFFIX}`,
    description: 'The terms for using this portfolio site.',
    icon: FileText,
  },
]

export const externals: ExternalRoute[] = [
  {
    kind: 'external',
    id: 'framework',
    href: externalLinks.framework,
    label: 'Framework',
    description: 'The agentic framework I build with.',
    icon: Compass,
    group: 'discover',
  },
]

/** Sidebar entries per group, in registry order (pages first, then external links). */
export function navEntries(group: NavGroupId): NavEntry[] {
  return [...pages.filter((p) => p.group === group), ...externals.filter((e) => e.group === group)]
}

/** Where a nav entry goes: its Home section when it has one, else its page. */
export function navTarget(page: PageRoute): string {
  return page.section ? `/#${page.section}` : page.path
}

export const notFoundTitle = `Page Not Found | ${SUFFIX}`
